/**
 * Hand decomposition — shared across rulesets. Pure tile combinatorics: a
 * standard hand is 4 sets + 1 pair. A kong counts as one set but occupies
 * 4 tiles. Exposed melds are given; the concealed tiles must supply whatever
 * sets remain, plus the pair. None of this depends on fan/point values or
 * house rules, which is why it lives here rather than under hkos/ — MCR
 * needs the exact same shape enumeration.
 *
 * A hand can often be read more than one way (e.g. 111222333m is three pungs
 * OR three identical chows) and the readings score differently. So we
 * enumerate every valid decomposition and let the scorer pick the best.
 */

import {
  type Tile,
  countTiles,
  isHonour,
  rankOf,
  sortTiles,
  suitOf,
} from '../tiles';
import type { Meld } from '../types';

export interface Decomposition {
  /** Exactly 4 entries: chow / pung / kong, in no particular order. */
  sets: Meld[];
  pair: Meld;
}

const MAX_DECOMPOSITIONS = 64; // guard against pathological inputs

/**
 * Enumerate all ways the concealed tiles complete the hand around the
 * already-exposed melds. Returns [] if no valid reading exists.
 */
export function decompose(concealed: Tile[], melds: Meld[]): Decomposition[] {
  const setsNeeded = 4 - melds.filter((m) => m.type !== 'pair').length;
  if (setsNeeded < 0) return [];

  const counts = countTiles(concealed);
  const results: Decomposition[] = [];
  const seen = new Set<string>();

  for (const [tile, n] of counts) {
    if (n < 2) continue;
    const remaining = new Map(counts);
    remaining.set(tile, n - 2);
    if (remaining.get(tile) === 0) remaining.delete(tile);

    for (const sets of extractSets(remaining, setsNeeded)) {
      const decomp: Decomposition = {
        sets: [...melds.filter((m) => m.type !== 'pair'), ...sets],
        pair: { type: 'pair', tiles: [tile, tile], concealed: true },
      };
      const key = fingerprint(decomp);
      if (seen.has(key)) continue;
      seen.add(key);
      results.push(decomp);
      if (results.length >= MAX_DECOMPOSITIONS) return results;
    }
  }

  return results;
}

/** Recursively pull `needed` sets out of a tile multiset. */
function extractSets(counts: Map<Tile, number>, needed: number): Meld[][] {
  if (needed === 0) {
    return counts.size === 0 ? [[]] : [];
  }
  if (counts.size === 0) return [];

  // Always consume the lowest remaining tile — this makes the enumeration
  // exhaustive without generating permutations of the same decomposition.
  const lowest = sortTiles([...counts.keys()])[0]!;
  const out: Meld[][] = [];

  // Try a pung.
  if ((counts.get(lowest) ?? 0) >= 3) {
    const next = take(counts, [lowest, lowest, lowest]);
    if (next) {
      for (const rest of extractSets(next, needed - 1)) {
        out.push([{ type: 'pung', tiles: [lowest, lowest, lowest], concealed: true }, ...rest]);
      }
    }
  }

  // Try a chow (numbered suits only).
  if (!isHonour(lowest) && rankOf(lowest) <= 7) {
    const s = suitOf(lowest);
    const r = rankOf(lowest);
    const run: Tile[] = [`${r}${s}`, `${r + 1}${s}`, `${r + 2}${s}`];
    const next = take(counts, run);
    if (next) {
      for (const rest of extractSets(next, needed - 1)) {
        out.push([{ type: 'chow', tiles: run, concealed: true }, ...rest]);
      }
    }
  }

  return out;
}

/** Remove tiles from a count map, or return null if not all are present. */
function take(counts: Map<Tile, number>, tiles: Tile[]): Map<Tile, number> | null {
  const next = new Map(counts);
  for (const t of tiles) {
    const n = next.get(t) ?? 0;
    if (n === 0) return null;
    if (n === 1) next.delete(t);
    else next.set(t, n - 1);
  }
  return next;
}

function fingerprint(d: Decomposition): string {
  const sets = d.sets
    .map((m) => `${m.type}:${m.concealed ? 'c' : 'o'}:${m.tiles.join('')}`)
    .sort()
    .join('|');
  return `${sets}//${d.pair.tiles.join('')}`;
}

/** All tiles a decomposition accounts for, melds included. */
export function decompositionTiles(d: Decomposition): Tile[] {
  return [...d.sets.flatMap((m) => m.tiles), ...d.pair.tiles];
}
